import React from 'react';
import { useIntl } from 'react-intl';
import colors from 'resources/colors';
import { AnalysisData, AnalysisBearingAnomalyDetectionValue } from 'models/analysis';
import { ChartHeaderPanel } from 'components/chart/ChartHeaderPanel';
import { LegendGroup, ChartLegendList } from '../common/ChartLegendList';
import { RexBearingAnomalyDetectionChart } from './RexBearingAnomalyDetectionChart';
import { RexBearingAnomalyDetectionFooterChart } from './RexBearingAnomalyDetectionFooterChart';
import { ChartContainer } from '../common/ChartContainer';
import msgId from 'resources/intl';
import { useDispatch } from 'react-redux';
import {
  setRexBearingAnomalyDetectionExpanded,
  useAnalysisRexBearingAnomalyDetectionExpanded,
} from 'features/contents/analysis/analysisSlice';
import { ConfirmedSearchResultItem } from 'models/search';
import { styled } from '@mui/material';
import dimens from 'resources/dimens';
import config from 'resources/config';
import { dumpAnalysisRexBearingAnomalyDetection } from 'utils/dumpData';
import backgroundImage from 'resources/assets/200ZCA_AS.png';

const RootDiv = styled('div')({
  width: '100%',
});

const ContainerDiv = styled('div')({
  height: '100%',
  width: '100%',
});

const ContentsDiv = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  background: colors.chart.header.background,
  '&:last-child': {
    borderBottom: `0px solid ${colors.chart.divider}`,
  },
  zIndex: 2,
  overflow: 'hidden',
});

const HoverDiv = styled('div')({
  position: 'absolute',
  color: 'white',
  background: 'black',
  zIndex: 2,
  fontFamily: 'Arial, sans-serif',
  fontSize: 13,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

interface RexBearingAnomalyDetectionContainerProps {
  chartId: string;
  confirmedSearchResultItem: ConfirmedSearchResultItem;
  analysisData: AnalysisData;
}
interface ChartProps {
  titleId: string;
  port: AnalysisBearingAnomalyDetectionValue;
  starboard: AnalysisBearingAnomalyDetectionValue;
}

export function RexBearingAnomalyDetectionContainer(
  props: RexBearingAnomalyDetectionContainerProps
): JSX.Element {
  const { chartId, confirmedSearchResultItem, analysisData } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const expanded = useAnalysisRexBearingAnomalyDetectionExpanded();
  const contents = analysisData.data.rexBearingAnomalyDetection;
  const imageUrl = 'url(' + backgroundImage + ')';

  if (contents == null) {
    return <div></div>;
  }

  const analysisName =
    intl.formatMessage({
      id: 'AnalysisSection' + analysisData.data.rexBearingAnomalyDetection?.analysisSectionName,
    }) +
    ' : ' +
    intl.formatMessage({
      id: 'AnalysisMenu' + analysisData.data.rexBearingAnomalyDetection?.analysisMenuName,
    });

  const graphTypes: ChartProps[] = [
    {
      titleId: msgId.rexBearingAnomalyDetectionBearing7,
      port: contents.Bearing7P,
      starboard: contents.Bearing7S,
    },
    {
      titleId: msgId.rexBearingAnomalyDetectionBearing8,
      port: contents.Bearing8P,
      starboard: contents.Bearing8S,
    },
  ];

  if (config.valueInspectionLog) {
    dumpAnalysisRexBearingAnomalyDetection(analysisData);
  }

  /**
   * CSV出力
   */
  const handleCsvOutput = () => {
    // noop
  };

  /**
   * 開閉状態変更
   * @param expanded 開閉状態
   */
  const handleExpandChanged = (expanded: boolean) => {
    dispatch(setRexBearingAnomalyDetectionExpanded(expanded));
  };

  /**
   * 凡例リスト
   */
  const legendGroups: LegendGroup = {
    groupName: analysisData.shipName + ' - ' + analysisData.machineName,
    legends: []
  };

  const panel = 'RexBearingAnomalyDetection';
  const PlotL = 'Plot' + panel + msgId.rexBearingAnomalyDetectionBearing7.substring(0,msgId.rexBearingAnomalyDetectionBearing7.length - 1);
  const hoverId = PlotL + 'hover';

  return (
    <RootDiv>
      <ChartContainer
        chartId={chartId}
        shipName={analysisData.shipName}
        machineName={analysisData.machineName}
        analysisName={analysisName}
        confirmedSearchResultItem={confirmedSearchResultItem}
        title={analysisName}
        expanded={expanded}
        onExpandChanged={handleExpandChanged}
      >
        <ContainerDiv>
          <ChartHeaderPanel
            chartId={chartId}
            sx={{ paddingRight: `${dimens.chart.legend.width}px` }}
            title={analysisName}
            titleEditable={false}
            csvOutputEnabled={false}
            csvOutputLocked={true}
          />
          <HoverDiv id={hoverId} />
          {graphTypes.map((type, index) => {
            return (
              <ContentsDiv key={index}>
                <RexBearingAnomalyDetectionChart
                  titleId={type.titleId}
                  dates={contents.date}
                  sx={{
                    width: `calc(100% - ${dimens.chart.legend.width}px - 1px)`,
                    borderRight: `1px solid ${colors.chart.divider}`,
                  }}
                  ShaftPort={type.port}
                  ShaftStarboard={type.starboard}
                />
                {/* <ChartDivider orientation="vertical" flexItem /> */}
                {index === 0 &&
                <ChartLegendList
                  legendGroups={[legendGroups]}
                  backgroundImage={imageUrl}
                  backgroundImageHeight="128px"
                />}
              </ContentsDiv>
            );
          })}
          <ContentsDiv>
            <RexBearingAnomalyDetectionFooterChart
              titleId={'Footer'}
              sx={{
                width: `calc(100% - ${dimens.chart.legend.width}px - 1px)`,
                borderRight: `1px solid ${colors.chart.divider}`,
              }}
              dates={contents.date}
            />
            <ChartLegendList legendGroups={[]} />
          </ContentsDiv>
        </ContainerDiv>
      </ChartContainer>
    </RootDiv>
  );
}
